import {
  Calendar, ClipboardList, Users, BookOpen, Search,
  Inbox, HelpCircle, BarChart3, Bell, Frown, WifiOff
} from 'lucide-react';
import Button from './Button';

const EmptyState = ({
  icon,
  title,
  description,
  action,
  actionLabel,
  secondaryAction,
  secondaryActionLabel,
  illustration,
  className = '',
}) => {
  return (
    <div className={`text-center py-16 px-6 ${className}`}>
      {illustration ? (
        <div className="mb-6">{illustration}</div>
      ) : (
        <div className="inline-flex items-center justify-center w-16 h-16 bg-neutral-100 dark:bg-neutral-800 rounded-2xl mb-6">
          {icon || <Inbox size={28} strokeWidth={1.5} className="text-neutral-400" />}
        </div>
      )}

      <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-100 mb-2">{title}</h3>

      {description && (
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-8 max-w-md mx-auto leading-relaxed">{description}</p>
      )}

      {(action || secondaryAction) && (
        <div className="flex items-center justify-center gap-3">
          {action && (
            <Button variant="primary" onClick={action}>
              {actionLabel || 'Get Started'}
            </Button>
          )}
          {secondaryAction && (
            <Button variant="ghost" onClick={secondaryAction}>
              {secondaryActionLabel || 'Learn More'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export const NoSessionsEmpty = ({ onCreateSession }) => (
  <EmptyState
    icon={<Calendar size={28} strokeWidth={1.5} className="text-neutral-400" />}
    title="No Sessions Yet"
    description="Get started by creating your first training session. You can add activities, upload workbooks, and invite participants."
    action={onCreateSession}
    actionLabel="Create First Session"
  />
);

export const NoActivitiesEmpty = ({ onAddActivity }) => (
  <EmptyState
    icon={<ClipboardList size={28} strokeWidth={1.5} className="text-neutral-400" />}
    title="No Activities Added"
    description="Start building your session by adding activities. Choose from templates or create custom activities."
    action={onAddActivity}
    actionLabel="Add Activity"
  />
);

export const NoParticipantsEmpty = ({ onInviteParticipants }) => (
  <EmptyState
    icon={<Users size={28} strokeWidth={1.5} className="text-neutral-400" />}
    title="No Participants Yet"
    description="Invite participants to join this session via email or share the session link."
    action={onInviteParticipants}
    actionLabel="Invite Participants"
  />
);

export const NoWorkbooksEmpty = ({ onUploadWorkbook }) => (
  <EmptyState
    icon={<BookOpen size={28} strokeWidth={1.5} className="text-neutral-400" />}
    title="No Workbooks Uploaded"
    description="Upload PDF or PowerPoint files for participants to view during the session. Files are view-only and cannot be downloaded."
    action={onUploadWorkbook}
    actionLabel="Upload Workbook"
  />
);

export const NoResultsEmpty = ({ onClearFilters, searchQuery }) => (
  <EmptyState
    icon={<Search size={28} strokeWidth={1.5} className="text-neutral-400" />}
    title="No Results Found"
    description={
      searchQuery
        ? `No results found for "${searchQuery}". Try adjusting your search or filters.`
        : 'No results found matching your criteria. Try adjusting your filters.'
    }
    action={onClearFilters}
    actionLabel="Clear Filters"
  />
);

export const NoDataEmpty = ({ title, description, action, actionLabel }) => (
  <EmptyState
    icon={<Inbox size={28} strokeWidth={1.5} className="text-neutral-400" />}
    title={title || 'No Data Available'}
    description={description || 'There is no data to display at the moment.'}
    action={action}
    actionLabel={actionLabel}
  />
);

export const NoQuestionsEmpty = ({ onAskQuestion }) => (
  <EmptyState
    icon={<HelpCircle size={28} strokeWidth={1.5} className="text-neutral-400" />}
    title="No Questions Yet"
    description="Be the first to ask a question! Your questions help everyone learn."
    action={onAskQuestion}
    actionLabel="Ask a Question"
  />
);

export const NoAnalyticsEmpty = () => (
  <EmptyState
    icon={<BarChart3 size={28} strokeWidth={1.5} className="text-neutral-400" />}
    title="No Analytics Data Yet"
    description="Analytics will be available once the session starts and participants begin engaging with activities."
  />
);

export const NoNotificationsEmpty = () => (
  <EmptyState
    icon={<Bell size={28} strokeWidth={1.5} className="text-neutral-400" />}
    title="All Caught Up!"
    description="You don't have any new notifications. We'll let you know when something important happens."
  />
);

export const SessionNotFoundEmpty = ({ onBackToSessions }) => (
  <EmptyState
    icon={<Frown size={28} strokeWidth={1.5} className="text-neutral-400" />}
    title="Session Not Found"
    description="The session you're looking for doesn't exist or has been removed."
    action={onBackToSessions}
    actionLabel="Back to Sessions"
  />
);

export const ConnectionErrorEmpty = ({ onRetry }) => (
  <EmptyState
    icon={<WifiOff size={28} strokeWidth={1.5} className="text-red-400" />}
    title="Connection Error"
    description="Unable to connect to the server. Please check your internet connection and try again."
    action={onRetry}
    actionLabel="Retry"
  />
);

export default EmptyState;
